import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useWindowDimensions, View } from 'react-native'

import { cardsDifferenceWithWinners, PotKind } from '@ppwr/poker'

import { translate } from '../../../i18n'
import { useStore } from '../../../models'
import { styles } from './HandWinner.style'
import { InformationGameOver } from './partial/InformationGame/InformationGameOver'
import { DS } from '../../../design-system'
import { ChipsLayout } from '../Board/ChipsLayout'
import { BoardLayoutConstants, TexasHoldEmAmount, ThreeCardAmount } from '../Board'
import { PokerCard } from '@/components/GameAssets/Cards/PokerCard'
import { Suit, Value } from 'pokersolver'
import { isWeb } from '@/components/WebWrapper'

export interface HandWinnerProps {
  colorText?: 'black' | 'white'
  winnerCardSizes?: 16 | 24 | 32
  isGameOverScreen?: boolean
}

export const HandWinner = observer(
  ({ colorText = 'white', winnerCardSizes = 32, isGameOverScreen }: HandWinnerProps) => {
    const {
      game: {
        communityCards: savedCommunityCards,
        winners: savedWinners,
        finalWinnerInfo,
        myPlayer,
        mainPot,
        isGameOver,
        isThreeCard
      }
    } = useStore()

    const windowSize = useWindowDimensions()

    // Checking to see if this is a normal Hand Winners or Final Game Over Hand Winners
    const communityCardsStateValue = savedCommunityCards.length
      ? savedCommunityCards
      : (finalWinnerInfo?.communityCards ?? [])

    const winnersStateValue = savedWinners.length
      ? savedWinners
      : finalWinnerInfo?.player
        ? [finalWinnerInfo?.player]
        : []

    // Saving in state to avoid a new snapshot changing the value.
    const [communityCards] = useState(communityCardsStateValue)
    const [winners] = useState(winnersStateValue)

    const mainPotWinners = winners.filter((w) => w.potKind === PotKind.MAIN)
    const mainPotWinner = mainPotWinners[0]

    const amountOfCardsMissing = (isThreeCard ? ThreeCardAmount : TexasHoldEmAmount) - (communityCards?.length ?? 0)
    const boardCards = [...communityCards, ...Array.from({ length: amountOfCardsMissing }).map(() => undefined)]

    //if (!mainPotWinner) return null

    const { cards, playerName, winningCards, playerId } = mainPotWinner ?? {
      cards: undefined,
      playerName: '',
      winningCards: [],
      playerId: 'onePlayerRemaining'
    }

    if (mainPotWinner?.cards.length === 0 && playerName !== 'onePlayerRemaining')
      // Everyone folded
      return (
        <DS.Frame style={styles.textWin}>
          <DS.Text variant="Heading 5/Medium" style={{ ...styles.cardsText, color: colorText }}>
            {playerId === myPlayer?.id ? 'You' : playerName} won
          </DS.Text>
          <DS.Text variant="Mini/Medium" style={styles.textFolded}>
            Everyone else folded
          </DS.Text>
        </DS.Frame>
      )

    // if (!cards || !winningCards || !communityCards) return null

    const playerNames = mainPotWinners.reduce((message, winner, index) => {
      const playerName = winner.userId === myPlayer?.id ? 'You' : winner.playerName
      if (index === 0) return playerName
      if (index < mainPotWinners.length - 1) return `${message}, ${playerName}`
      return `${message} and ${playerName}`
    }, '')

    const isPotSplit = mainPotWinners.length > 1

    const winnerName =
      playerId === 'onePlayerRemaining'
        ? 'Game Over'
        : playerId === myPlayer?.id
          ? `You ${translate('handWinner.youText')}\n`
          : `${playerName} ${translate('handWinner.youText')}`

    const playerOrPlayersWinningText = isPotSplit
      ? `${translate('common.sidepotPlayers', {
          playerNames
        })} `
      : winnerName

    const winnerHoleCards = cardsDifferenceWithWinners(cards, winningCards)
    const winnerCommunityCards = cardsDifferenceWithWinners(communityCards, winningCards)

    const cardWidth = isWeb ? 38 : BoardLayoutConstants.getCardWidth(windowSize.width)

    return (
      <View style={styles.container}>
        <View style={styles.cardsContainer}>
          <View style={styles.cards}>
            <PokerCard.Flip
              key="hand-winner-card-0"
              suit={cards?.[0]?.[1] as Suit | undefined}
              value={cards?.[0]?.[0] as Value | undefined}
              side={cards?.[0] !== undefined ? 'front' : 'back'}
              size={winnerCardSizes * 2}
              containerStyle={{
                transform: [{ rotate: '-5deg' }],
                opacity: !winnerHoleCards.includes(cards?.[0]) ? 0.6 : 1
              }}
            />
            <PokerCard.Flip
              key="hand-winner-card-1"
              suit={cards?.[1]?.[1] as Suit | undefined}
              value={cards?.[1]?.[0] as Value | undefined}
              side={cards?.[1] !== undefined ? 'front' : 'back'}
              size={winnerCardSizes * 2}
              containerStyle={{
                transform: [{ rotate: '5deg' }],
                opacity: !winnerHoleCards.includes(cards?.[1]) ? 0.6 : 1
              }}
            />
          </View>
          <DS.Frame>
            <DS.Text variant={'Heading 5/Medium'} style={{ ...styles.cardsText, color: colorText }}>
              {playerOrPlayersWinningText}
            </DS.Text>
            <DS.Frame style={styles.handWinnerView}>
              <DS.Text variant={'Mini/Medium'} style={{ ...styles.handWinnerText, color: colorText }}>
                {mainPotWinner?.rankedHandName && translate(`gameWinningHand.${mainPotWinner.rankedHandName}`)}
              </DS.Text>
            </DS.Frame>
          </DS.Frame>
          {isGameOver ? (
            <>
              <ChipsLayout />
              <DS.Text variant="Heading 4/Regular" color="Brand/Cream">
                {mainPotWinner?.chipsWon ?? 0}
              </DS.Text>
            </>
          ) : null}
        </View>
        {isGameOver && (
          <View style={styles.gameOverCardsContainer}>
            {boardCards.map((card, i) => (
              <PokerCard.Flip
                key={`game-over-card-${i}`}
                suit={card?.[1] as Suit | undefined}
                value={card?.[0] as Value | undefined}
                side={card ? 'front' : 'back'}
                flipOptions={{
                  duration: 0
                }}
                size={cardWidth}
                containerStyle={{
                  opacity: card && !winnerCommunityCards.includes(card) ? 0.6 : 1
                }}
              />
            ))}
          </View>
        )}
        {isGameOverScreen ? <InformationGameOver /> : null}
      </View>
    )
  }
)
